/**
 * Store Locator (Mapbox) — Osmo Supply resource.
 * Visit A Showroom block.
 */

(function () {
  "use strict";

  const blocksOnPage = window.SOJ_BLOCKS_ON_PAGE;
  if (
    Array.isArray(blocksOnPage) &&
    !blocksOnPage.includes("visit-a-showroom")
  ) {
    return;
  }

  const MAPBOX_VERSION = "3.20.0";
  const MAPBOX_JS =
    "https://api.mapbox.com/mapbox-gl-js/v" + MAPBOX_VERSION + "/mapbox-gl.js";
  const MAPBOX_CSS =
    "https://api.mapbox.com/mapbox-gl-js/v" + MAPBOX_VERSION + "/mapbox-gl.css";

  let mapboxLoader = null;

  function loadMapbox() {
    if (window.mapboxgl) {
      return Promise.resolve(window.mapboxgl);
    }

    if (mapboxLoader) {
      return mapboxLoader;
    }

    mapboxLoader = new Promise((resolve, reject) => {
      if (!document.querySelector("link[data-soj-mapbox-css]")) {
        const css = document.createElement("link");
        css.rel = "stylesheet";
        css.href = MAPBOX_CSS;
        css.setAttribute("data-soj-mapbox-css", "1");
        document.head.appendChild(css);
      }

      const existing = document.querySelector("script[data-soj-mapbox-js]");
      if (existing) {
        existing.addEventListener("load", () => resolve(window.mapboxgl), {
          once: true,
        });
        existing.addEventListener(
          "error",
          () => reject(new Error("Mapbox GL failed to load")),
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = MAPBOX_JS;
      script.async = true;
      script.setAttribute("data-soj-mapbox-js", "1");
      script.onload = () => resolve(window.mapboxgl);
      script.onerror = () => {
        mapboxLoader = null;
        reject(new Error("Mapbox GL failed to load"));
      };
      document.head.appendChild(script);
    });

    return mapboxLoader;
  }

  function whenMapNearViewport(element, callback) {
    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      callback();
      return;
    }

    const observer = new IntersectionObserver(
      (entries, obs) => {
        if (!entries.some((entry) => entry.isIntersecting)) {
          return;
        }
        obs.disconnect();
        callback();
      },
      { rootMargin: "400px 0px" }
    );

    observer.observe(element);
  }

  function readMapboxConfig(wrapper) {
    const token = (
      (wrapper && wrapper.dataset.mapboxToken) ||
      (window.SOJ_STORE_LOCATOR && window.SOJ_STORE_LOCATOR.mapboxToken) ||
      ""
    ).trim();
    const mapStyle = (
      (wrapper && wrapper.dataset.mapStyle) ||
      (window.SOJ_STORE_LOCATOR && window.SOJ_STORE_LOCATOR.mapStyle) ||
      "mapbox://styles/sonofesse/cmsoitp4u014g01s88llggq3w"
    ).trim();

    return { token, mapStyle };
  }

  function initStoreLocatorPagination(wrapper) {
    const api = {
      onSelect: null,
      setActive: function () {},
      scrollTo: function () {},
    };

    const collection = wrapper.querySelector(".store-locator__collection");
    const pagination = wrapper.querySelector("[data-locator-pagination]");
    if (!collection || !pagination) {
      return api;
    }

    const items = Array.from(wrapper.querySelectorAll("[data-locator-item]"));
    const dots = Array.from(
      pagination.querySelectorAll(".store-locator__pagination-dot")
    );
    if (items.length < 2 || dots.length === 0) {
      return api;
    }

    let raf = null;
    let ignoreScroll = false;

    function setActive(index) {
      dots.forEach((dot, i) => {
        const on = i === index;
        dot.classList.toggle("is-active", on);
        if (on) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
    }

    function getActiveIndex() {
      const origin = collection.getBoundingClientRect().left;
      let best = 0;
      let bestDist = Infinity;
      items.forEach((item, i) => {
        const dist = Math.abs(item.getBoundingClientRect().left - origin);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    function scrollTo(index) {
      const item = items[index];
      if (!item) return;

      const delta =
        item.getBoundingClientRect().left -
        collection.getBoundingClientRect().left;
      ignoreScroll = true;
      collection.scrollTo({
        left: collection.scrollLeft + delta,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "auto"
          : "smooth",
      });
      setActive(index);
      window.setTimeout(function () {
        ignoreScroll = false;
      }, 500);
    }

    dots.forEach((dot, i) => {
      dot.addEventListener("click", function () {
        scrollTo(i);
        if (typeof api.onSelect === "function") {
          api.onSelect(i);
        }
      });
    });

    collection.addEventListener(
      "scroll",
      function () {
        if (ignoreScroll) return;
        if (raf != null) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          setActive(getActiveIndex());
        });
      },
      { passive: true }
    );

    api.setActive = setActive;
    api.scrollTo = scrollTo;
    return api;
  }

  function initStoreLocatorMapbox(wrapper, pagination) {
    const { token, mapStyle } = readMapboxConfig(wrapper);
    const cfg = {
      mapboxToken: token,
      mapStyle,
      projection: "mercator",
      fallbackCenter: [-1.8, 54.5],
      fallbackZoom: 4.5,
      selectedZoom: 14,
      pitch: 55,
      selectedPitch: 60,
      bearing: -20,
      terrainExaggeration: 1.3,
      flyToDuration: 1200,
      fitPadding: 64,
      fitMaxZoom: 13,
    };

    if (typeof mapboxgl === "undefined") {
      console.warn("[BLOCK] visit-a-showroom: Mapbox GL is not available.");
      return;
    }

    if (!cfg.mapboxToken || cfg.mapboxToken === "YOUR_MAPBOX_PUBLIC_TOKEN") {
      console.warn(
        "[BLOCK] visit-a-showroom: Mapbox token missing. Add it under Site Settings → Mapbox."
      );
      return;
    }

    mapboxgl.accessToken = cfg.mapboxToken;

    if (!wrapper || wrapper.dataset.locatorInit === "initialized") return;

    const mapEl = wrapper.querySelector("[data-locator-map]");
    if (!mapEl) return;

    const locations = readLocations(wrapper);
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const map = new mapboxgl.Map({
      container: mapEl,
      style: cfg.mapStyle,
      center: cfg.fallbackCenter,
      zoom: cfg.fallbackZoom,
      projection: cfg.projection,
      pitch: cfg.pitch,
      bearing: cfg.bearing,
      maxPitch: 85,
      attributionControl: false,
      cooperativeGestures: true,
    });

    map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    map.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      "top-left"
    );

    map.on("load", () => {
      enable3dTerrain(map, cfg.terrainExaggeration);

      const pins = addPins(map, locations, wrapper);

      locations.forEach((location, index) => {
        location.element.addEventListener("click", (event) => {
          if (event.target.closest("a, button, input, select, textarea")) return;
          selectLocation(index);
        });

        location.element.addEventListener("keydown", (event) => {
          if (event.target !== location.element) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          selectLocation(index);
        });
      });

      pins.forEach((pin, index) => {
        pin.element.addEventListener("click", () => {
          selectLocation(index, { scrollList: true });
        });

        pin.element.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          selectLocation(index, { scrollList: true });
        });
      });

      function selectLocation(index, options) {
        const location = locations[index];
        if (!location) return;

        pins.forEach((pin, pinIndex) => {
          const isActive = pinIndex === index;
          pin.element.setAttribute("data-active", String(isActive));
          pin.element.setAttribute("aria-pressed", String(isActive));
          locations[pinIndex].element.setAttribute(
            "data-active",
            String(isActive)
          );
        });

        pagination.setActive(index);
        if (options && options.scrollList) {
          pagination.scrollTo(index);
        }

        const camera = {
          center: [location.lng, location.lat],
          zoom: cfg.selectedZoom,
          pitch: cfg.selectedPitch,
          bearing: cfg.bearing,
        };

        if (reducedMotion) {
          map.jumpTo(camera);
        } else {
          map.flyTo({
            ...camera,
            duration: cfg.flyToDuration,
            essential: false,
          });
        }
      }

      pagination.onSelect = function (index) {
        selectLocation(index);
      };

      fitToLocations(map, locations, reducedMotion);
      wrapper.dataset.locatorInit = "initialized";
      console.log("[BLOCK] visit-a-showroom initialized");
    });

    let resizeTimer;
    window.addEventListener(
      "resize",
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => map.resize(), 150);
      },
      { passive: true }
    );

    function readLocations(wrapperEl) {
      return Array.from(wrapperEl.querySelectorAll("[data-locator-item]"))
        .map((element, index) => {
          const lat = Number.parseFloat(
            element.getAttribute("data-locator-lat")
          );
          const lng = Number.parseFloat(
            element.getAttribute("data-locator-lng")
          );

          return {
            id:
              element.getAttribute("data-locator-id") ||
              `location-${index + 1}`,
            name:
              text(element, "[data-locator-item-name]") ||
              `Location ${index + 1}`,
            lat,
            lng,
            element,
          };
        })
        .filter(
          (location) =>
            Number.isFinite(location.lat) && Number.isFinite(location.lng)
        );
    }

    function addPins(mapInstance, locationList, wrapperEl) {
      const template = wrapperEl.querySelector(
        "[data-locator-marker-template]"
      );

      return locationList.map((location) => {
        let element;

        if (template instanceof HTMLTemplateElement) {
          element = template.content.firstElementChild.cloneNode(true);
        } else if (template) {
          element = template.cloneNode(true);
          element.removeAttribute("data-locator-marker-template");
        } else {
          element = document.createElement("button");
          element.type = "button";
          element.className = "locator-marker";
          element.innerHTML =
            '<span class="locator-marker__dot" aria-hidden="true"></span>';
        }

        element.setAttribute("data-locator-marker", location.id);
        element.setAttribute("data-active", "false");
        element.setAttribute("aria-label", `Show ${location.name}`);
        element.setAttribute("aria-pressed", "false");

        if (!element.matches("button, a, input")) {
          element.setAttribute("role", "button");
          element.setAttribute("tabindex", "0");
        }

        const marker = new mapboxgl.Marker({
          element,
          anchor: "center",
        })
          .setLngLat([location.lng, location.lat])
          .addTo(mapInstance);

        return { marker, element, location };
      });
    }

    function enable3dTerrain(mapInstance, exaggeration) {
      if (!mapInstance.getSource("mapbox-dem")) {
        mapInstance.addSource("mapbox-dem", {
          type: "raster-dem",
          url: "mapbox://mapbox.mapbox-terrain-dem-v1",
          tileSize: 512,
          maxzoom: 14,
        });
      }

      mapInstance.setTerrain({
        source: "mapbox-dem",
        exaggeration: exaggeration,
      });

      // Soft atmosphere similar to Mapbox Studio 3D previews
      if (typeof mapInstance.setFog === "function") {
        mapInstance.setFog({
          color: "rgb(245, 240, 230)",
          "high-color": "rgb(180, 200, 220)",
          "horizon-blend": 0.08,
          "space-color": "rgb(20, 30, 45)",
          "star-intensity": 0.1,
        });
      }
    }

    function fitToLocations(mapInstance, locationList, prefersReduced) {
      if (!locationList.length) return;

      if (locationList.length === 1) {
        mapInstance.jumpTo({
          center: [locationList[0].lng, locationList[0].lat],
          zoom: cfg.selectedZoom,
          pitch: cfg.selectedPitch,
          bearing: cfg.bearing,
        });
        return;
      }

      const bounds = new mapboxgl.LngLatBounds();
      locationList.forEach((location) =>
        bounds.extend([location.lng, location.lat])
      );

      mapInstance.fitBounds(bounds, {
        padding: cfg.fitPadding,
        maxZoom: cfg.fitMaxZoom,
        duration: prefersReduced ? 0 : 700,
        pitch: cfg.pitch,
        bearing: cfg.bearing,
      });
    }

    function text(parent, selector) {
      const element = parent.querySelector(selector);
      return element ? element.textContent.trim() : "";
    }
  }

  // Pagination immediately; Mapbox only when the map is near the viewport.
  document.addEventListener("DOMContentLoaded", () => {
    const wrapper = document.querySelector("[data-locator-init]");
    if (!wrapper) return;

    const pagination = initStoreLocatorPagination(wrapper);
    const mapEl = wrapper.querySelector("[data-locator-map]");
    if (!mapEl) return;

    const { token } = readMapboxConfig(wrapper);
    if (!token || token === "YOUR_MAPBOX_PUBLIC_TOKEN") {
      console.warn(
        "[BLOCK] visit-a-showroom: Mapbox token missing. Add it under Site Settings → Mapbox."
      );
      return;
    }

    whenMapNearViewport(mapEl, () => {
      loadMapbox()
        .then(() => initStoreLocatorMapbox(wrapper, pagination))
        .catch((error) => {
          console.warn("[BLOCK] visit-a-showroom: Mapbox failed to load", error);
        });
    });
  });
})();
